import IconButton from '@alltrails/denali/components/IconButton';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import { type FormatDateOptions, FormattedMessage, useIntl } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import classNames from 'classnames';
import { StaticImageData } from 'next/image';
import { useLanguageCode } from '@alltrails/language/hooks/useLanguageCode';
import { useLanguageRegionCode } from '@alltrails/language/hooks/useLanguageRegionCode';
import AppIcon from '@alltrails/denali/components/AppIcon';
import Link from '@alltrails/denali/components/Link';
import { wrapUrlSafe } from '@alltrails/shared/utils/languageSupport';
import type { LanguageRegionCode } from '@alltrails/core/types/LanguageRegionCode';
import styles from './styles/styles.module.scss';
import isUnpublished from '@/utils/isUnpublishedPost';

export type ArticleCard = {
  cardType: 'article';
  logoSrc: StaticImageData;
  company: string;
  title: string;
  date: string;
  href: string;
};

export type PressReleaseCard = {
  cardType: 'press-release';
  title: string;
  date: string;
  slug: string;
  languages?: LanguageRegionCode[];
  status?: string;
};

type PressCardProps = ArticleCard | PressReleaseCard;

const ICON_SIZE = 32;

const PressCard = (props: PressCardProps) => {
  const { cardType, title, date } = props;

  const intl = useIntl();
  const subheader = cardType === 'article' ? props.company : <FormattedMessage defaultMessage="Press release" />;

  const formatOptions: FormatDateOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  const languageCode = useLanguageCode();
  const languageRegionCode = useLanguageRegionCode();

  const link = cardType === 'article' ? props.href : `/press/${props.slug}`;
  const isUnpublishedPressRelease = cardType === 'press-release' && isUnpublished(props.status);

  return (
    <div className={classNames(styles.container, { [styles.unpublished]: isUnpublishedPressRelease })}>
      <div
        className={classNames(styles.contentContainer, {
          [styles.pressRelease]: cardType === 'press-release',
          [styles.withHyphens]: languageCode === 'de' // hyphenate German titles due to long words
        })}
        lang={languageCode}
      >
        <div className={styles.logoContainer}>
          {cardType === 'article' ? (
            <Image
              alt={intl.formatMessage({ defaultMessage: '{companyName} logo' }, { companyName: props.company })}
              src={props.logoSrc}
              loading="lazy"
              height={ICON_SIZE}
              width={ICON_SIZE}
            />
          ) : (
            <AppIcon size={ICON_SIZE} shape="circle" />
          )}
          <Typography variant="heading200">{subheader}</Typography>
        </div>
        <h3 className={styles.headingContainer}>
          <Link href={link} noUnderline>
            {' '}
            <span className={styles.heading} dangerouslySetInnerHTML={{ __html: title }}></span>
          </Link>
        </h3>
      </div>
      <div className={styles.contentContainer}>
        <Typography variant="paragraph200">{intl.formatDate(date, formatOptions)}</Typography>
        {cardType === 'article' ? (
          <IconButton
            icon={{ Component: ArrowSq, orientation: 'right' }}
            testId="press-card-link"
            title="Press card link"
            size="sm"
            className={styles.iconButton}
            linkInfo={{ href: link, target: '_blank' }}
          />
        ) : (
          <IconButton
            icon={{ Component: ArrowSq, orientation: 'right' }}
            testId="press-card-button"
            title="Press card button"
            size="sm"
            className={styles.iconButton}
            linkInfo={{ href: wrapUrlSafe(link, languageRegionCode) }}
          />
        )}
      </div>
    </div>
  );
};

export default PressCard;
